import { area, bboxClip, bboxPolygon } from "@turf/turf";

// Print areas are squares in Web Mercator meters, matching the bounding boxes
// raster_coverage_print_options builds server side
const EARTH_RADIUS = 6378137;
const METERS_PER_MILE = 1609.34;

export const PRINT_DISTANCES = [0.25, 0.5, 0.75, 1.0, 2.0, 2.5, 5.0, 10.0];
export const MIN_PRINT_DPI = 200;
export const PRINT_INCHES = 9;

export function toMercator([lng, lat]) {
  return [
    (EARTH_RADIUS * lng * Math.PI) / 180,
    EARTH_RADIUS * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360)),
  ];
}

export function fromMercator([x, y]) {
  return [
    ((x / EARTH_RADIUS) * 180) / Math.PI,
    ((2 * Math.atan(Math.exp(y / EARTH_RADIUS)) - Math.PI / 2) * 180) / Math.PI,
  ];
}

export function squareAroundPoint(lngLat, distanceMiles) {
  const [x, y] = toMercator(lngLat);
  const half = distanceMiles * METERS_PER_MILE;
  return [x - half, y - half, x + half, y + half];
}

// Square anchored at the drag start, growing toward the pointer by the larger
// of the two drag distances
export function squareFromDrag(startLngLat, endLngLat) {
  const [x0, y0] = toMercator(startLngLat);
  const [x1, y1] = toMercator(endLngLat);
  const side = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
  const x2 = x0 + Math.sign(x1 - x0 || 1) * side;
  const y2 = y0 + Math.sign(y1 - y0 || 1) * side;
  return [
    Math.min(x0, x2),
    Math.min(y0, y2),
    Math.max(x0, x2),
    Math.max(y0, y2),
  ];
}

export function squareSideMiles(bbox) {
  return (bbox[2] - bbox[0]) / METERS_PER_MILE;
}

// Web Mercator stretches distances by 1/cos(latitude), so scale back to real
// ground distance at the square's center
export function groundSideMeters(bbox) {
  const [, centerLat] = fromMercator([0, (bbox[1] + bbox[3]) / 2]);
  return (bbox[2] - bbox[0]) * Math.cos((centerLat * Math.PI) / 180);
}

export function groundSideMiles(bbox) {
  return groundSideMeters(bbox) / METERS_PER_MILE;
}

// Scale of the square printed PRINT_INCHES wide, e.g. 1:12,000
export function printScale(bbox) {
  const groundInches = groundSideMeters(bbox) / 0.0254;
  const ratio = groundInches / PRINT_INCHES;
  const feetPerInch = ratio / 12;

  return {
    ratio: Number(ratio.toPrecision(3)),
    perInch:
      feetPerInch >= 2640
        ? `${(feetPerInch / 5280).toFixed(feetPerInch >= 52800 ? 0 : 1)} mi`
        : `${Number(feetPerInch.toPrecision(2)).toLocaleString()} ft`,
  };
}

export function lngLatBounds(bbox) {
  const [west, south] = fromMercator([bbox[0], bbox[1]]);
  const [east, north] = fromMercator([bbox[2], bbox[3]]);
  return [west, south, east, north];
}

export function printAreaGeoJSON(bbox, { handles = false } = {}) {
  const [west, south, east, north] = lngLatBounds(bbox);
  const square = [
    [west, south],
    [east, south],
    [east, north],
    [west, north],
    [west, south],
  ];
  const world = [
    [-180, -85],
    [180, -85],
    [180, 85],
    [-180, 85],
    [-180, -85],
  ];

  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        properties: { kind: "mask" },
        geometry: { type: "Polygon", coordinates: [world, square] },
      },
      {
        type: "Feature",
        properties: { kind: "outline" },
        geometry: { type: "Polygon", coordinates: [square] },
      },
      ...(handles ? handleFeatures(west, south, east, north) : []),
    ],
  };
}

function handleFeatures(west, south, east, north) {
  const midLng = (west + east) / 2;
  const midLat = (south + north) / 2;

  return [
    [west, south],
    [east, south],
    [east, north],
    [west, north],
    [midLng, south],
    [midLng, north],
    [west, midLat],
    [east, midLat],
  ].map((coordinates) => ({
    type: "Feature",
    properties: { kind: "handle" },
    geometry: { type: "Point", coordinates },
  }));
}

export const RESIZE_CURSORS = {
  n: "ns-resize",
  s: "ns-resize",
  e: "ew-resize",
  w: "ew-resize",
  ne: "nesw-resize",
  sw: "nesw-resize",
  nw: "nwse-resize",
  se: "nwse-resize",
};

// Which edge or corner of the square a screen point is on, if any. `project`
// converts [lng, lat] to screen pixels (map.project).
export function squareHandleAt(bbox, point, project, tolerance) {
  const [west, south, east, north] = lngLatBounds(bbox);
  const topLeft = project([west, north]);
  const bottomRight = project([east, south]);

  const withinX =
    point.x >= topLeft.x - tolerance && point.x <= bottomRight.x + tolerance;
  const withinY =
    point.y >= topLeft.y - tolerance && point.y <= bottomRight.y + tolerance;
  if (!withinX || !withinY) return null;

  const vertical =
    Math.abs(point.y - topLeft.y) <= tolerance
      ? "n"
      : Math.abs(point.y - bottomRight.y) <= tolerance
        ? "s"
        : "";
  const horizontal =
    Math.abs(point.x - topLeft.x) <= tolerance
      ? "w"
      : Math.abs(point.x - bottomRight.x) <= tolerance
        ? "e"
        : "";

  return vertical + horizontal || null;
}

export function squareContains(bbox, point, project) {
  const [west, south, east, north] = lngLatBounds(bbox);
  const topLeft = project([west, north]);
  const bottomRight = project([east, south]);

  return (
    point.x > topLeft.x &&
    point.x < bottomRight.x &&
    point.y > topLeft.y &&
    point.y < bottomRight.y
  );
}

// Resizes the square by dragging one edge or corner, keeping the opposite
// edge or corner fixed. Edges stay centered on the other axis.
export function resizeSquare(bbox, handle, lngLat, minSideMiles) {
  const [xmin, ymin, xmax, ymax] = bbox;
  const [px, py] = toMercator(lngLat);
  const minSide = minSideMiles * METERS_PER_MILE;
  const cx = (xmin + xmax) / 2;
  const cy = (ymin + ymax) / 2;
  const clamp = (side) => Math.max(side, minSide);

  switch (handle) {
    case "e": {
      const side = clamp(px - xmin);
      return [xmin, cy - side / 2, xmin + side, cy + side / 2];
    }
    case "w": {
      const side = clamp(xmax - px);
      return [xmax - side, cy - side / 2, xmax, cy + side / 2];
    }
    case "n": {
      const side = clamp(py - ymin);
      return [cx - side / 2, ymin, cx + side / 2, ymin + side];
    }
    case "s": {
      const side = clamp(ymax - py);
      return [cx - side / 2, ymax - side, cx + side / 2, ymax];
    }
    case "ne": {
      const side = clamp(Math.max(px - xmin, py - ymin));
      return [xmin, ymin, xmin + side, ymin + side];
    }
    case "nw": {
      const side = clamp(Math.max(xmax - px, py - ymin));
      return [xmax - side, ymin, xmax, ymin + side];
    }
    case "se": {
      const side = clamp(Math.max(px - xmin, ymax - py));
      return [xmin, ymax - side, xmin + side, ymax];
    }
    case "sw": {
      const side = clamp(Math.max(xmax - px, ymax - py));
      return [xmax - side, ymax - side, xmax, ymax];
    }
    default:
      return bbox;
  }
}

export function footprintCoverage(footprints, bbox) {
  const bounds = lngLatBounds(bbox);
  const covered = footprints.reduce(
    (sum, footprint) => sum + area(bboxClip(footprint, bounds)),
    0,
  );
  return covered / area(bboxPolygon(bounds));
}
